import * as ko from "knockout";
import PromiseRequest from "../../../../req/PromiseRequest";
import {IKnockoutComponent, IMessageData, IObjectData, IResponse} from "../../../../index";
import {KOSubscriptionTracker} from "../../../../util/KOSubscriptionTracker";
import {MAX_RESULTS_PER_PAGE, ObjectTypeId} from "../../../../constants";
import {createErrorMessage, getObjectDefinition, IObjectDefinition} from "../../../../util/misc";
import {global, lm} from "../../../../appContext";
import {getSearchObjectParams} from "../../../../util/request";
import {DynamicProperties} from "./DynamicProperties";
import {nsFa} from "../../../fa/fn";
import {IFormPartParameters} from "../../view/part/index";
import AbstractText from "../../../dynamic-variable/AbstractText";
import {TComponentName} from "../../../../constants/ComponentRegistry";
import {EMPTY_STRING, REQUEST_MESSAGE} from "../../../../constants/Language";
import i = nsFa.i;
import previewHtml from './preview.html?raw';

export let template = previewHtml;

interface IParams {
    dynamicProperties: DynamicProperties
}

export class viewModel {
    private ids: number[] | null = null;
    private readonly dynamicProperties: DynamicProperties;
    private readonly isReady: KnockoutObservable<boolean> = ko.observable(false);
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private readonly reqDisplayName = new PromiseRequest('evaluateexpression');
    private readonly reqExpression = new PromiseRequest('evaluateexpression');
    private readonly reqSearchObject: PromiseRequest = global.getPromiseRequest('searchobject');

    private readonly displayName: KnockoutObservable<string | null> = ko.observable(null);
    private readonly evaluation: KnockoutObservable<string | null> = ko.observable(null);
    private readonly expression: KnockoutObservable<string | null> = ko.observable(null);
    private readonly objectId: KnockoutObservable<number | null> = ko.observable(null);

    private objDef: KnockoutObservable<IObjectDefinition | null> = ko.observable(null);
    private objectTypeId: KnockoutObservable<ObjectTypeId | null> = ko.observable(null);
    private memoryObjectId: Partial<Record<ObjectTypeId, number>> = {};

    constructor(params: IParams) {
        this.dynamicProperties = params.dynamicProperties;
        this.kosub.subscribe(global.login.selectedOrganization, () => {
            this.objectId(null);
            this.displayName(null);
            this.evaluation(null);
            this.expression(null);
            this.ids = null;
        });

        this.kosub.subscribeChanged(global.textEditor, (newEditor: AbstractText | null) => this.onEditor(newEditor))

        if (global.textEditor()) {
            this.onEditor(global.textEditor());
        }

        this.kosub.subscribe(this.objectId, (objectId: number | null) => {
            const objectTypeId = this.objectTypeId();
            if (objectTypeId != null) {
                if (objectId != null) {
                    this.memoryObjectId[objectTypeId] = objectId;
                } else {
                    delete this.memoryObjectId[objectTypeId];
                }
            }
            this.expression(null);
            this.fetchDisplayName();
            this.fetchEvaluation();
        })
        this.kosub.subscribe(this.dynamicProperties.textRange, () => {
            this.fetchEvaluation();
        });
        if (this.objectId()) {
            this.fetchDisplayName();
            this.fetchEvaluation();
        }
        this.isReady = ko.pureComputed((): boolean => {
            return !!global.login.selectedOrganization();
        });
    }

    private onEditor(editor: AbstractText | null) {
        if (editor) {
            const objectTypeId = ko.unwrap(editor.objectTypeId);
            if (this.objectTypeId() != objectTypeId) {
                this.objectTypeId(objectTypeId);
                this.objDef(getObjectDefinition(objectTypeId));
                this.ids = null;
                this.displayName(null);
                this.evaluation(null);
                this.expression(null);
                const currentTypeId = this.objectTypeId();
                this.objectId(currentTypeId != null ? (this.memoryObjectId[currentTypeId] ?? null) : null);
            }
        }
    }

    private onPickObject() {
        const objDef = this.objDef();
        if (!objDef) {
            return;
        }
        this.killRequests();
        global.dialogManager.addComponent({
            title: `${i(objDef.icon)} ${lm.t(`choose subject|s:((${objDef.name}|ac))`)}`,
            content: {
                name: 'component/object/dialog/pick',
                params: {
                    ...(this.objectTypeId() != null ? {objecttypeid: this.objectTypeId()!} : {}),
                    onObjectSelected: (obj: IObjectData) => this.objectId(obj.id),
                }
            }
        });
    }

    private onPickRandomClick() {
        if (this.ids) {
            this.pickRandomId();
            return;
        }
        this.messageData({message: REQUEST_MESSAGE});
        this.reqSearchObject.abort();
        this.reqSearchObject
            .request(getSearchObjectParams({
                ...(this.objectTypeId() != null ? {objecttypeid: this.objectTypeId()!} : {}),
                onlyids: true,
                resultsprpage: MAX_RESULTS_PER_PAGE,
            }))
            .then((data: number[]) => {
                this.ids = data;
                this.messageData(null);
                this.pickRandomId();
            })
            .catch((reason: string) => this.messageData(createErrorMessage(String(reason))));
    }

    private onStopPreviewClick() {
        this.objectId(null);
        this.displayName(null);
        this.evaluation(null);
        this.killRequests();
    }

    private pickRandomId() {
        if (!this.ids?.length) {
            return;
        }
        const randomIndex = Math.floor(Math.random() * this.ids.length);
        const randomId = this.ids[randomIndex];
        if (randomId === undefined) {
            return;
        }
        this.objectId(randomId ?? null);
    }

    private fetchDisplayName() {
        if (!this.objectId()) {
            this.displayName(null);
            return;
        }

        const objDef = this.objDef();
        if (!objDef) {
            return;
        }

        this.reqDisplayName.abort();
        this.reqDisplayName
            .request({
                expression: objDef.displayNameExpression,
                objectid: this.objectId(),
            })
            .then((data: string[]) => {
                this.displayName(data[0] ?? null);
            })
            .catch((reason: string) => {
                this.displayName(reason)
            });
    }

    private fetchEvaluation() {
        const expressionNew = this.dynamicProperties.textRange()?.wrapperStr ?? '';
        const expressionOld = this.expression();
        const isUnchanged = expressionNew == expressionOld;
        if (isUnchanged) {
            return;
        }

        this.expression(expressionNew);
        this.messageData(null);

        if (!this.objectId()) {
            return;
        }

        this.evaluation(null);

        this.reqExpression.abort();
        this.reqExpression
            .request({
                expression: this.expression(),
                objectid: this.objectId(),
            }, true)
            .then((resp: IResponse) => {
                if (parseInt(resp.returncode)) {
                    throw(resp);
                }
                const result = resp.data[0].trim();
                this.evaluation(result  || lm.t(EMPTY_STRING));
            })
            .catch((resp: IResponse) => {
                if (parseInt(resp.returncode) == 99) {
                    this.evaluation('invalid expression');
                } else {
                    this.evaluation(resp.lasterror);
                }
            });
    }

    private killRequests() {
        this.reqDisplayName.abort();
        this.reqExpression.abort();
        this.reqSearchObject.abort();
    }

    private getFormElementComponentParameters(): IKnockoutComponent {
        const params: IFormPartParameters = {expression: this.evaluation() ?? '', preview: true};
        const name = 'component/form/view/part/' + this.dynamicProperties.getName('lower');
        return {
            name: name as TComponentName,
            params: params,
        }
    }

    private isFormElement(): boolean {
        return this.dynamicProperties.syncfield()?.objecttypeid == ObjectTypeId.FormFunction;
    }

    dispose() {
        this.kosub.unsuscribeAll();
        this.killRequests();
    }
}
